import React from "react";
import { Stack } from "react-bootstrap";
import { colors } from "src/styles";
import styled from "styled-components";

interface WidgetProps {
  caption: string;
}

interface ComplianceServiceRow {
  serviceName: string;
  scheduled: number;
  completed: number;
}

const ComplianceTrackerTable = ({ caption }: WidgetProps) => {
  const serviceRows: ComplianceServiceRow[] = [
    { serviceName: "Backflow Preventors", scheduled: 24, completed: 22 },
    { serviceName: "CO/NO2 Sensors", scheduled: 18, completed: 16 },
    { serviceName: "Compressors", scheduled: 12, completed: 11 },
    { serviceName: "ESA", scheduled: 8, completed: 8 },
    { serviceName: "Fire Dampers", scheduled: 30, completed: 28 },
    { serviceName: "Generators", scheduled: 6, completed: 5 },
    { serviceName: "Grease Traps", scheduled: 15, completed: 13 },
    { serviceName: "HVAC", scheduled: 36, completed: 33 },
    { serviceName: "Interior Plants", scheduled: 20, completed: 18 },
    { serviceName: "Lifting Equipment", scheduled: 10, completed: 9 },
    { serviceName: "Oil and Water Separators", scheduled: 14, completed: 12 },
    { serviceName: "Pest Control", scheduled: 26, completed: 25 },
    { serviceName: "Pressure Vessels", scheduled: 8, completed: 7 },
    { serviceName: "Roof Anchors", scheduled: 12, completed: 12 },
    { serviceName: "Roof Drains", scheduled: 18, completed: 16 },
    { serviceName: "Septic Systems", scheduled: 4, completed: 4 },
    { serviceName: "Sump Pumps", scheduled: 16, completed: 15 },
    { serviceName: "Trench Drains", scheduled: 10, completed: 8 },
    { serviceName: "Water Treatment", scheduled: 22, completed: 20 },
    { serviceName: "Well Water", scheduled: 6, completed: 5 },
  ];

  return (
    <WidgetContainer>
      <ContentStack gap={3}>
        <HeaderRow>
          <h1 style={{ fontWeight: 600, margin: 0 }}>{caption}</h1>
          <StaticDataBadge>Static Data</StaticDataBadge>
        </HeaderRow>
        <TableContainer>
          <ComplianceTable>
            <thead>
              <tr>
                <TableHeaderCell>Services</TableHeaderCell>
                <TableHeaderCell>Scheduled</TableHeaderCell>
                <TableHeaderCell>Completed</TableHeaderCell>
                <TableHeaderCell>Percentage</TableHeaderCell>
              </tr>
            </thead>
            <tbody>
              {serviceRows.map((row) => {
                const percentage = row.scheduled > 0 ? `${Math.round((row.completed / row.scheduled) * 100)}%` : "0%";

                return (
                  <tr key={row.serviceName}>
                    <ServiceCell>{row.serviceName}</ServiceCell>
                    <TableCell>{row.scheduled}</TableCell>
                    <TableCell>{row.completed}</TableCell>
                    <TableCell>{percentage}</TableCell>
                  </tr>
                );
              })}
            </tbody>
          </ComplianceTable>
        </TableContainer>
      </ContentStack>
    </WidgetContainer>
  );
};

export default ComplianceTrackerTable;

const WidgetContainer = styled.div`
  display: flex;
  height: 100%;
  min-width: 0;
  border: 1px solid ${colors.grey.light11};
  border-top: 4px solid ${colors.kleenway.green};
  padding: 16px;
  border-radius: 6px;
`;

const ContentStack = styled(Stack)`
  width: 100%;
  min-width: 0;
`;

const TableContainer = styled.div`
  width: 100%;
  max-width: 100%;
  min-width: 0;
  max-height: 420px;
  overflow-x: auto;
  overflow-y: auto;
`;

const ComplianceTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  min-width: 720px;
`;

const TableHeaderCell = styled.th`
  position: sticky;
  top: 0;
  z-index: 1;
  background: ${colors.grey.light6};
  color: ${colors.black.dark1};
  font-size: 13px;
  font-weight: 600;
  text-align: left;
  padding: 10px 12px;
  border: 1px solid ${colors.grey.light11};
`;

const TableCell = styled.td`
  color: ${colors.grey.dark};
  font-size: 13px;
  padding: 10px 12px;
  border: 1px solid ${colors.grey.light11};
  vertical-align: middle;
`;

const ServiceCell = styled(TableCell)`
  font-weight: 500;
  color: ${colors.black.dark1};
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
`;

const StaticDataBadge = styled.span`
  background-color: ${colors.grey.light11};
  color: ${colors.grey.dark};
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
`;
